import { z } from 'zod';
import { Aggregation } from '@models/feature-descriptor';
import { Column, TableColumns } from '@models/column';
import { KeysOfUnion } from '@utils/types';
import {
  ColumnDraft,
  NumberDraft,
  OnlyColumnFields,
} from './common-field-draft';

type AggregationColumnFields = OnlyColumnFields<Aggregation>;

export type AggregationDraft = Partial<Record<KeysOfUnion<Aggregation>, any>>;

const DisctanceDraft = z.object({
  latitutde_1_column: ColumnDraft,
  longitude_1_column: ColumnDraft,
  latitutde_2_column: ColumnDraft,
  longitude_2_column: ColumnDraft,
});

const TemporalChangeDraft = z.object({
  source_time_column: ColumnDraft,
  target_time_column: ColumnDraft,
  source_column: ColumnDraft,
  upper_bound: z.string(),
  lower_bound: z.string(),
  range_shift: z.string(),
});

const LatestKDraft = z.object({
  source_time_filed: ColumnDraft,
  source_field: ColumnDraft,
  k: NumberDraft,
});

const TopicWeightDraft = z.object({
  text_topic_field: ColumnDraft,
  original_text_column_name: ColumnDraft,
  index: NumberDraft,
});

const AggregationDrafts: Record<Aggregation.Type, z.ZodObject<any>> = {
  [Aggregation.Type.AbsoluteTemporalChangeCountRecords]:
    TemporalChangeDraft.omit({ source_column: true }).merge(
      z.object({
        type: z.literal(Aggregation.Type.AbsoluteTemporalChangeCountRecords),
      })
    ),
  [Aggregation.Type.AbsoluteTemporalChangeMax]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.AbsoluteTemporalChangeMax),
    })
  ),
  [Aggregation.Type.AbsoluteTemporalChangeMean]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.AbsoluteTemporalChangeMean),
    })
  ),
  [Aggregation.Type.AbsoluteTemporalChangeMin]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.AbsoluteTemporalChangeMin),
    })
  ),
  [Aggregation.Type.AbsoluteTemporalChangeSum]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.AbsoluteTemporalChangeSum),
    })
  ),
  [Aggregation.Type.AnyBetween]: z.object({
    type: z.literal(Aggregation.Type.AnyBetween),
    field: ColumnDraft,
    lower_edge: NumberDraft,
    upper_edge: NumberDraft,
  }),
  [Aggregation.Type.AnyValue]: z.object({
    type: z.literal(Aggregation.Type.AnyValue),
    field: ColumnDraft,
    value: NumberDraft,
  }),
  [Aggregation.Type.AsIs]: z.object({
    type: z.literal(Aggregation.Type.AsIs),
    column: ColumnDraft,
  }),
  [Aggregation.Type.C3]: z.object({
    type: z.literal(Aggregation.Type.C3),
    source_time_field: ColumnDraft,
    source_field: ColumnDraft,
    lag: NumberDraft,
  }),
  [Aggregation.Type.CWT]: z.object({
    type: z.literal(Aggregation.Type.CWT),
    source_time_field: ColumnDraft,
    source_field: ColumnDraft,
    index: NumberDraft,
    width: NumberDraft,
  }),
  [Aggregation.Type.ClosestTimeDifference]: z.object({
    type: z.literal(Aggregation.Type.ClosestTimeDifference),
    source_field: ColumnDraft,
    target_field: ColumnDraft,
    shift: z.string(),
  }),
  [Aggregation.Type.Count]: z.object({
    type: z.literal(Aggregation.Type.Count),
    field: ColumnDraft,
  }),
  [Aggregation.Type.CountBetween]: z.object({
    type: z.literal(Aggregation.Type.CountBetween),
    field: ColumnDraft,
    lower_edge: NumberDraft,
    upper_edge: NumberDraft,
  }),
  [Aggregation.Type.CountOfValue]: z.object({
    type: z.literal(Aggregation.Type.CountOfValue),
    field: ColumnDraft,
    value: NumberDraft,
  }),
  [Aggregation.Type.CountRecords]: z.object({
    type: z.literal(Aggregation.Type.CountRecords),
    table: z.string(),
  }),
  [Aggregation.Type.CountUnique]: z.object({
    type: z.literal(Aggregation.Type.CountUnique),
    column: ColumnDraft,
  }),
  [Aggregation.Type.DayOfWeek]: z.object({
    type: z.literal(Aggregation.Type.DayOfWeek),
    column: ColumnDraft,
  }),
  [Aggregation.Type.Distance]: DisctanceDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.Distance) })
  ),
  [Aggregation.Type.Exist]: z.object({
    type: z.literal(Aggregation.Type.Exist),
    table: z.string(),
  }),
  [Aggregation.Type.GridTargetMeanEncode]: z.object({
    type: z.literal(Aggregation.Type.GridTargetMeanEncode),
    latitude_field: ColumnDraft,
    longitude_field: ColumnDraft,
    encoder_id: NumberDraft,
    distance: z.string(),
  }),
  [Aggregation.Type.HistogramEqualityRatio]: z.object({
    type: z.literal(Aggregation.Type.HistogramEqualityRatio),
    field: ColumnDraft,
    value: NumberDraft,
  }),
  [Aggregation.Type.Hour]: z.object({
    type: z.literal(Aggregation.Type.Hour),
    column: ColumnDraft,
  }),
  [Aggregation.Type.LatestKMax]: LatestKDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.LatestKMax),
    })
  ),
  [Aggregation.Type.LatestKMaxMinusMin]: LatestKDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.LatestKMaxMinusMin),
    })
  ),
  [Aggregation.Type.LatestKMean]: LatestKDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.LatestKMean),
    })
  ),
  [Aggregation.Type.LatestKMin]: LatestKDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.LatestKMin),
    })
  ),
  [Aggregation.Type.LatestKStd]: LatestKDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.LatestKStd),
    })
  ),
  [Aggregation.Type.LatestKSum]: LatestKDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.LatestKSum),
    })
  ),
  [Aggregation.Type.LatestTimeDifference]: z.object({
    type: z.literal(Aggregation.Type.LatestTimeDifference),
    source_field: ColumnDraft,
    target_field: ColumnDraft,
    shift: z.string(),
  }),
  [Aggregation.Type.Max]: z.object({
    type: z.literal(Aggregation.Type.Max),
    column: ColumnDraft,
  }),
  [Aggregation.Type.MaxDistance]: DisctanceDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.MaxDistance) })
  ),
  [Aggregation.Type.MaxInterval]: z.object({
    type: z.literal(Aggregation.Type.MaxInterval),
    column: ColumnDraft,
  }),
  [Aggregation.Type.Mean]: z.object({
    type: z.literal(Aggregation.Type.Mean),
    field: ColumnDraft,
  }),
  [Aggregation.Type.MeanDistance]: DisctanceDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.MeanDistance) })
  ),
  [Aggregation.Type.MeanInterval]: z.object({
    type: z.literal(Aggregation.Type.MeanInterval),
    column: ColumnDraft,
  }),
  [Aggregation.Type.MeanTimeDifference]: z.object({
    type: z.literal(Aggregation.Type.MeanTimeDifference),
    column1: ColumnDraft,
    column2: ColumnDraft,
    unit: z.string(),
  }),
  [Aggregation.Type.Min]: z.object({
    type: z.literal(Aggregation.Type.Min),
    column: ColumnDraft,
  }),
  [Aggregation.Type.MinDistance]: DisctanceDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.MinDistance) })
  ),
  [Aggregation.Type.MinInterval]: z.object({
    type: z.literal(Aggregation.Type.MinInterval),
    field: ColumnDraft,
  }),
  [Aggregation.Type.Month]: z.object({
    type: z.literal(Aggregation.Type.Month),
    field: ColumnDraft,
  }),

  [Aggregation.Type.N1RedundantMean]: z.object({
    type: z.literal(Aggregation.Type.N1RedundantMean),
    field: ColumnDraft,
  }),
  [Aggregation.Type.N1RedundantNullRatio]: z.object({
    type: z.literal(Aggregation.Type.N1RedundantNullRatio),
    field: ColumnDraft,
  }),
  [Aggregation.Type.N1RedundantRatioOfValue]: z.object({
    type: z.literal(Aggregation.Type.N1RedundantRatioOfValue),
    field: ColumnDraft,
    ratio_value: z.union([NumberDraft, z.string(), z.boolean()]),
    null_inclusion: z.boolean(),
  }),
  [Aggregation.Type.N1RedundantSum]: z.object({
    type: z.literal(Aggregation.Type.N1RedundantSum),
    field: ColumnDraft,
  }),
  [Aggregation.Type.N1RedundantTopicWeightMean]: z.object({
    type: z.literal(Aggregation.Type.N1RedundantTopicWeightMean),
    text_topic_field: ColumnDraft,
    original_text_column_name: ColumnDraft,
    index: NumberDraft,
  }),
  [Aggregation.Type.N1RedundantTopicWeightSum]: z.object({
    type: z.literal(Aggregation.Type.N1RedundantTopicWeightSum),
    text_topic_field: ColumnDraft,
    original_text_column_name: ColumnDraft,
    index: NumberDraft,
  }),
  [Aggregation.Type.NgramCount]: z.object({
    type: z.literal(Aggregation.Type.NgramCount),
    field: ColumnDraft,
    ngram: z.array(z.string()),
  }),

  [Aggregation.Type.NonBusinessDays]: z.object({
    type: z.literal(Aggregation.Type.NonBusinessDays),
    column: ColumnDraft,
  }),
  [Aggregation.Type.NullRatio]: z.object({
    type: z.literal(Aggregation.Type.NullRatio),
    field: ColumnDraft,
  }),
  [Aggregation.Type.RatioBetween]: z.object({
    type: z.literal(Aggregation.Type.RatioBetween),
    field: ColumnDraft,
    lower_edge: NumberDraft,
    upper_edge: NumberDraft,
  }),
  [Aggregation.Type.RatioOfValue]: z.object({
    type: z.literal(Aggregation.Type.RatioOfValue),
    field: ColumnDraft,
    ratio_value: z.union([NumberDraft, z.string(), z.boolean()]),
    null_inclusion: z.boolean(),
  }),
  [Aggregation.Type.Std]: z.object({
    type: z.literal(Aggregation.Type.Std),
    field: ColumnDraft,
  }),
  [Aggregation.Type.StdDistance]: DisctanceDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.StdDistance) })
  ),
  [Aggregation.Type.StdInterval]: z.object({
    type: z.literal(Aggregation.Type.StdInterval),
    column: ColumnDraft,
  }),
  [Aggregation.Type.StdTimeDifference]: z.object({
    type: z.literal(Aggregation.Type.StdInterval),
    column1: ColumnDraft,
    column2: ColumnDraft,
    unit: z.string(),
  }),
  [Aggregation.Type.Sum]: z.object({
    type: z.literal(Aggregation.Type.Sum),
    column: ColumnDraft,
  }),
  [Aggregation.Type.SumDistance]: DisctanceDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.SumDistance) })
  ),
  [Aggregation.Type.TargetMeanEncode]: z.object({
    type: z.literal(Aggregation.Type.TargetMeanEncode),
    field: ColumnDraft,
    encode_field: NumberDraft,
  }),
  [Aggregation.Type.TargetTimeDifference]: z.object({
    type: z.literal(Aggregation.Type.TargetTimeDifference),
    source_field: ColumnDraft,
    target_field: ColumnDraft,
    shift: z.string(),
  }),

  [Aggregation.Type.TemporalChangeCountRecords]: TemporalChangeDraft.omit({
    source_column: true,
  }).merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeCountRecords),
    })
  ),
  [Aggregation.Type.TemporalChangeMax]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeMax),
    })
  ),
  [Aggregation.Type.TemporalChangeMean]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeMean),
    })
  ),
  [Aggregation.Type.TemporalChangeMin]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeMin),
    })
  ),
  [Aggregation.Type.TemporalChangeSum]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeSum),
    })
  ),

  [Aggregation.Type.TemporalChangeRatioCountRecords]: TemporalChangeDraft.omit({
    source_column: true,
  }).merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeRatioCountRecords),
    })
  ),
  [Aggregation.Type.TemporalChangeRatioMax]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeRatioMax),
    })
  ),
  [Aggregation.Type.TemporalChangeRatioMean]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeRatioMean),
    })
  ),
  [Aggregation.Type.TemporalChangeRatioMin]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeRatioMin),
    })
  ),
  [Aggregation.Type.TemporalChangeRatioSum]: TemporalChangeDraft.merge(
    z.object({
      type: z.literal(Aggregation.Type.TemporalChangeRatioSum),
    })
  ),
  [Aggregation.Type.TimeDifference]: z.object({
    type: z.literal(Aggregation.Type.TimeDifference),
    column1: ColumnDraft,
    column2: ColumnDraft,
    unit: z.string(),
  }),

  [Aggregation.Type.TopicWeightMax]: TopicWeightDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.TopicWeightMax) })
  ),

  [Aggregation.Type.TopicWeightMean]: TopicWeightDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.TopicWeightMean) })
  ),
  [Aggregation.Type.TopicWeightMin]: TopicWeightDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.TopicWeightMin) })
  ),
  [Aggregation.Type.TopicWeightSum]: TopicWeightDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.TopicWeightSum) })
  ),
  [Aggregation.Type.TopicWeightValue]: TopicWeightDraft.merge(
    z.object({ type: z.literal(Aggregation.Type.TopicWeightValue) })
  ),
};

export namespace AggregationDraft {
  export function fromAggregation(
    aggregation: Aggregation,
    tableColumns: TableColumns
  ): AggregationDraft {
    const schema = AggregationDrafts[aggregation.type];

    const columnFields = Object.entries(schema.shape)
      .filter(([_, field]: [any, any]) => field.description === 'Column')
      .map(([colName]) => colName) as Array<keyof AggregationColumnFields>;

    return {
      ...aggregation,
      ...Object.fromEntries(
        columnFields.map(field => [
          field,
          Column.fromFullName(aggregation[field], tableColumns) ?? {},
        ])
      ),
    };
  }

  export function validate(aggregationDraft: AggregationDraft) {
    if (!aggregationDraft.type) {
      return null;
    }
    const schema = AggregationDrafts[aggregationDraft.type as Aggregation.Type];
    return schema?.safeParse(aggregationDraft);
  }
}
